/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router';

import { useFacilityAlerts, NotFoundPage } from 'sarsaparilla';
import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl';
import { useDocumentTitle } from 'shared-ui/utils/seo-utils';
import { useSendGAEvent } from 'shared-ui/utils/gaCommon';

import { INVENTORY_TIMED_ENTRY } from 'ui-field-sales/src/constants/types';

import { fetchFacilityData } from '../../actions/facilityDataActions';
import { fetchToursByFacility } from '../../actions/toursDataActions';
import { fetchPricingView } from '../../actions/pricingViewActions';
import { FacilityPage } from '../../components/FacilityPage/FacilityPage';

export function FacilityPageContainer() {
    const alerts = useFacilityAlerts();
    const { facilityId } = useParams();
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const facilityImageData = useSelector((state) => state.timedEntry.facilityImages);
    const facilityData = useSelector((state) => state.timedEntry.facility);
    const recAreaData = useSelector((state) => state.timedEntry.recArea);
    const reviewSummaryData = useSelector((state) => state.timedEntry.reviewSummary);
    const agencyName = useSelector((state) => state.timedEntry.facility.agency_name);
    const wasGaEventFired = useRef(false);

    const tabFromQuery = searchParams.get('tab');

    const {
        isLoading: isFacilityLoading,
        facility_id: reduxFacilityId,
        facility_name: facilityName,
        facility_description: facilityDescription,
        parent_asset_id: parentId,
        error,
    } = facilityData;

    const {
        isLoading: isRecAreaLoading,
        name: parentName,
        city,
        state_code: state,
    } = recAreaData;

    const {
        isLoading: isReviewSummaryLoading,
        rating,
        reviewCount,
        mobileCoverageRating,
    } = reviewSummaryData;

    const {
        galleryImages,
        heroImage,
        loadingStatus: imageLoadingStatus,
    } = facilityImageData;

    /**
     * If there is no redux data OR the redux facilityId doesn't match the URL,
     * we fetch all the data
     */
    useEffect(() => {
        if (!reduxFacilityId || reduxFacilityId !== facilityId) {
            dispatch(fetchFacilityData(facilityId));
            dispatch(fetchToursByFacility(facilityId));
            dispatch(fetchPricingView(facilityId));
        }
    }, [dispatch, reduxFacilityId, facilityId]);

    const facilityNearLocation = state && city ? `${city}, ${state}` : null;

    useDocumentTitle(`${facilityName} - Recreation.gov`);

    // Fire the view_item GA Event */
    useSendGAEvent({
        itemLevel: 'Facility',
        facilityId,
        itemCategory: INVENTORY_TIMED_ENTRY,
    });

    if (error?.message) {
        return <NotFoundPage />;
    }

    return (
        <FacilityPage
            isLoading={isFacilityLoading || isReviewSummaryLoading || isRecAreaLoading}
            tabFromQuery={tabFromQuery}
            facilityId={facilityId}
            showBackToSearch={false}
            alerts={alerts}
            facilityName={facilityName}
            parentName={parentName}
            facilityDescription={facilityDescription}
            facilityNearLocation={facilityNearLocation}
            parentUrl={parentId ? getGatewayUrl(parentId) : null}
            rating={rating}
            reviewCount={reviewCount}
            mobileCoverageRating={mobileCoverageRating}
            galleryImages={galleryImages}
            heroImage={heroImage}
            facilityImageLoadingStatus={imageLoadingStatus}
        />
    );
}
